import React, { useState, useEffect, useLayoutEffect, Fragment } from "react";
import get from "src/lib/get";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import memoizeOne from "memoize-one";
import isEqual from "fast-deep-equal";
import { Helmet } from "react-helmet";
import toDecimalPlaces from "src/lib/to-decimal-places";
import MetaData from "src/react/components/meta-data";
import metaData from "src/data/meta-data";
import { createUpsellList } from "src/data/products-products";
import convertPenceToPounds from "src/lib/convert-pence-to-pounds";
import { getProductPage } from "src/services/contentful";
import { getProductReviews } from "src/services/reviews/product-reviews";
import { getSignupSelectedProductsSkus } from "src/redux/signup/selectors";
import { getIsUserAccountActive } from "src/redux/user/selectors";
import { LayoutWrapper } from "src/react/layout";
import { LoadingBlock } from "src/react/components/loading";
import CouponAppliedMessage from "src/react/pages/signup/components/coupon-applied-message";
import Reviews from "./components/reviews";
import Intro from "./components/intro";
import ProductFaqs from "./components/product-faqs";
import ContentBlock from "./components/content-block";
import ContentBlocks from "./components/content-blocks";
import ProductUpsellCarousel from "./components/product-upsell-carousel";

function getProductContentSchema({
  url = "",
  ratingValue = "",
  reviewCount = "",
  description = "",
  name = "",
  image = "",
  price = "",
}) {
  return `{
    "@context": "http://schema.org",
    "@type": "Product",
    "url": "${url}",
    "brand": {
      "@type": "Thing",
      "name": "Cornerstone"
    },
    ${
      ratingValue && reviewCount
        ? `"aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "${ratingValue}",
      "ratingCount": "${reviewCount}",
      "reviewCount": "${reviewCount}"
    },`
        : ``
    }
    "description": "${description}",
    "name": "${name}",
    "image": "${image}",
    "offers": {
      "@type": "Offer",
      "url": "${url}",
      "availability": "http://schema.org/InStock",
      "price": "${price}",
      "priceCurrency": "GBP"
    }
  }`;
}

const createMemoizedUpsellList = memoizeOne(
  (basketSkus, upsells) => createUpsellList(basketSkus, upsells),
  isEqual
);

export function Product({
  basketSkus = [],
  match,
  location,
  history,
  isUserAccountActive,
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState({});
  const [upsellItems, setUpsellItems] = useState([]);
  const [basket] = useState(basketSkus);
  const [reviewsData, setReviewsData] = useState({ reviews: {}, stats: {} });

  const productPageSlug = get(match, "params.productPageSlug", "");
  const addToBasketLink = get(page, "addToBasketLink");
  const metaImage = get(page, "metaImage.fields.file.url");
  const metaTags = metaData.product({
    title: page.metaTitle,
    description: page.metaDescription,
    ...(page.slug && { slug: page.slug }),
  });
  const faqs = get(page, "faqs", []);
  const upsells = get(page, "upsellOrder", []);
  const productContentSchema = getProductContentSchema({
    url: window && window.location.href,
    ratingValue: toDecimalPlaces(parseFloat(reviewsData.stats.average), 1),
    reviewCount: reviewsData.stats.count,
    description: page.metaDescription,
    name: page.title,
    image: metaImage,
    price: convertPenceToPounds(page.priceInPence),
  });

  useLayoutEffect(() => {
    setIsLoading(true);
    const fetchContent = () => {
      const isPreview = location.pathname.startsWith("/preview/");

      getProductPage(productPageSlug, isPreview).then(({ data, error }) => {
        if (error) {
          return history.replace("/404");
        }
        setPage(data);
        setIsLoading(false);
      });
    };
    fetchContent();
  }, [productPageSlug, history, location]);

  useEffect(() => {
    if (page.reviewsSku) {
      getProductReviews({ sku: page.reviewsSku }).then(({ data }) => {
        if (data) {
          setReviewsData(data);
        }
      });
    }
  }, [page.reviewsSku]);

  useEffect(() => {
    setUpsellItems(createMemoizedUpsellList(basket, upsells));
  }, [basket, upsells]);

  return (
    <Fragment>
      <MetaData data={metaTags}>
        {!isLoading && (
          <script type="application/ld+json">{productContentSchema}</script>
        )}
      </MetaData>
      {!isLoading && page.allowIndexing === false && (
        <Helmet>
          {" "}
          <meta name="robots" content="noindex" />
        </Helmet>
      )}
      <CouponAppliedMessage />
      {isLoading ? (
        <LayoutWrapper backgroundColor="white" fullHeight={true}>
          <LoadingBlock />
        </LayoutWrapper>
      ) : (
        <LayoutWrapper backgroundColor="white">
          <Intro
            {...page}
            addToBasketLink={addToBasketLink}
            isUserAccountActive={isUserAccountActive}
            reviewsData={reviewsData}
          />
          {page.contentBlock && (
            <ContentBlock>{page.contentBlock}</ContentBlock>
          )}
          {page.contentBlocks && <ContentBlocks data={page.contentBlocks} />}
          {!page.hideReviews && (
            <Reviews reviews={reviewsData.reviews} stats={reviewsData.stats} />
          )}
          {!!faqs.length && <ProductFaqs faqs={faqs} />}
          {!!upsellItems.length && !isUserAccountActive && (
            <ProductUpsellCarousel products={upsellItems} />
          )}
        </LayoutWrapper>
      )}
    </Fragment>
  );
}

Product.propTypes = {
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.object.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }),
  basketSkus: PropTypes.arrayOf(PropTypes.string),
  isUserAccountActive: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  basketSkus: getSignupSelectedProductsSkus(state),
  isUserAccountActive: getIsUserAccountActive(state),
});

export default connect(mapStateToProps)(Product);
